import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveLease } from '@/hooks/useLeases';
import { useCreateMaintenance, useMaintenance } from '@/hooks/useMaintenance';
import { uploadTempPhoto } from '@/api/uploads';
import type { MaintenanceCategory } from '@/api/maintenance';

const CATEGORIES: MaintenanceCategory[] = [
  'plumbing',
  'electricity',
  'appliance',
  'heating',
  'window',
  'door',
  'internet',
  'other',
];

interface FormValues {
  title: string;
  description: string;
  // backend требует category (обязателен); раньше форма его не слала -> 422.
  category: MaintenanceCategory;
  // backend MaintenancePriority = low/normal/high/urgent. 'medium' бэкенд НЕ
  // принимает (422 при создании заявки).
  priority: 'low' | 'normal' | 'high' | 'urgent';
}

export function MaintenancePage() {
  const { t } = useTranslation();
  const { activeLease } = useActiveLease();
  const list = useMaintenance(activeLease?.id ?? null);
  const create = useCreateMaintenance();
  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { priority: 'normal', category: 'other' },
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const onSubmit = handleSubmit(async (values) => {
    if (!activeLease) return;
    // Фото грузим в /uploads/temp -> photo_paths (backend ждёт строки-пути, не
    // multipart-файлы -- иначе фото терялись).
    const photo_paths = photos.length
      ? await Promise.all(photos.map((p) => uploadTempPhoto(p)))
      : undefined;
    await create.mutateAsync({
      lease_id: activeLease.id,
      category: values.category,
      title: values.title,
      description: values.description || undefined,
      priority: values.priority,
      photo_paths,
    });
    reset();
    setPhotos([]);
  });

  return (
    <div className="space-y-4">
      <Card title={t('maintenance.new_request')}>
        <form onSubmit={onSubmit} className="space-y-3">
          <Input label={t('maintenance.title')} {...register('title', { required: true })} />
          <label className="block text-sm font-medium text-slate-700">
            {t('maintenance.description')}
            <textarea
              {...register('description', { required: true })}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            {t('maintenance.category_label')}
            <select
              {...register('category')}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`maintenance.category.${c}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            {t('maintenance.priority')}
            <select
              {...register('priority')}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="low">{t('maintenance.priority_values.low')}</option>
              <option value="normal">{t('maintenance.priority_values.normal')}</option>
              <option value="high">{t('maintenance.priority_values.high')}</option>
              <option value="urgent">{t('maintenance.priority_values.urgent')}</option>
            </select>
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
          />
          <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>
            {t('maintenance.attach_photos')} ({photos.length})
          </Button>
          <Button type="submit" className="w-full" loading={create.isPending}>
            {t('common.submit')}
          </Button>
        </form>
      </Card>

      <Card title={t('maintenance.my_requests')}>
        {list.isLoading ? <Spinner /> : null}
        {list.data && list.data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {list.data.map((m) => (
              <li key={m.id} className="py-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">{m.title}</p>
                  <Badge tone="info">{t(`maintenance.status.${m.status}`)}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{m.description}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">{t('web.empty.no_data')}</p>
        )}
      </Card>
    </div>
  );
}
